export function RentDictatesValue() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Why Rents Dictate What Investors Will Pay
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Here's the harsh reality most apartment building owners don't understand: <strong>investors don't care about your granite countertops</strong>. They care about one thing—cash flow.
          </p>
        </div>

        {/* Key Concept */}
        <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-10 mb-16 border-2 border-teal-200">
          <div className="flex items-start gap-6 mb-8">
            <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-calculator-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-4">The Income Approach Formula</h3>
              <p className="text-xl text-gray-700 leading-relaxed">
                Commercial real estate investors use a simple formula to determine what they'll pay for your apartment building:
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-4">Property Value = Net Operating Income ÷ Cap Rate</div>
              <p className="text-lg text-gray-600">
                Your property's value is directly tied to the income it generates, not how pretty it looks.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="text-xl font-bold mb-3 text-teal-600">Net Operating Income</h4>
              <p className="text-gray-700">
                Annual rental income minus operating expenses (taxes, insurance, maintenance, management, utilities)
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="text-xl font-bold mb-3 text-teal-600">Cap Rate</h4>
              <p className="text-gray-700">
                Market-determined rate of return investors expect in your area (typically 5-10% for apartments)
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="text-xl font-bold mb-3 text-teal-600">Market Rents</h4>
              <p className="text-gray-700">
                What tenants will actually pay in your location, regardless of your renovation costs
              </p>
            </div>
          </div>
        </div>

        {/* The Problem Scenario */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-center">The Renovation Trap: A Real Example</h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Before Renovation */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                  <i className="ri-home-4-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Before Renovation</h4>
              </div>
              <div className="space-y-4 text-gray-700">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span>Current monthly rent per unit</span>
                  <strong>$800</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span>Number of units</span>
                  <strong>20 units</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span>Annual gross income</span>
                  <strong>$192,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span>Operating expenses (50%)</span>
                  <strong>-$96,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b-2 border-gray-400">
                  <span className="font-bold">Net Operating Income</span>
                  <strong className="text-xl">$96,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span>Market cap rate</span>
                  <strong>8%</strong>
                </div>
                <div className="flex justify-between pt-4">
                  <span className="text-xl font-bold">Property Value</span>
                  <strong className="text-2xl text-gray-600">$1,200,000</strong>
                </div>
              </div>
            </div>

            {/* After Renovation */}
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border-2 border-red-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-hammer-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">After $1.2M Renovation</h4>
              </div>
              <div className="space-y-4 text-gray-700">
                <div className="flex justify-between pb-3 border-b border-red-200">
                  <span>New monthly rent per unit</span>
                  <strong>$950</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-red-200">
                  <span>Number of units</span>
                  <strong>20 units</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-red-200">
                  <span>Annual gross income</span>
                  <strong>$228,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-red-200">
                  <span>Operating expenses (50%)</span>
                  <strong>-$114,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b-2 border-red-400">
                  <span className="font-bold">Net Operating Income</span>
                  <strong className="text-xl">$114,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-red-200">
                  <span>Market cap rate</span>
                  <strong>8%</strong>
                </div>
                <div className="flex justify-between pt-4">
                  <span className="text-xl font-bold">Property Value</span>
                  <strong className="text-2xl text-red-600">$1,425,000</strong>
                </div>
              </div>
            </div>
          </div>

          {/* The Shocking Math */}
          <div className="mt-8 bg-gradient-to-br from-red-100 to-red-50 rounded-2xl p-10 border-2 border-red-300">
            <h4 className="text-3xl font-bold mb-6 text-center text-red-700">The Shocking Reality</h4>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-5xl font-bold text-red-600 mb-2">$1,200,000</div>
                <div className="text-lg text-gray-700">Total Investment in Renovations</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-green-600 mb-2">$225,000</div>
                <div className="text-lg text-gray-700">Increase in Property Value</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-red-700 mb-2">-$975,000</div>
                <div className="text-lg text-gray-700">Net Loss on Renovation</div>
              </div>
            </div>
            <p className="text-center mt-8 text-xl text-gray-800 font-bold">
              You spent $1.2 million to increase the value by only $225,000. That's a <span className="text-red-600">$975,000 loss</span>!
            </p>
          </div>
        </div>

        {/* Why This Happens */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 mb-12">
          <h3 className="text-3xl font-bold mb-8 text-center">Why Renovations Often Don't Pay Off</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Market Rent Ceiling</h4>
                  <p className="text-gray-700">
                    Every neighborhood has a maximum rent tenants will pay, regardless of upgrades. A C-class area won't support A-class rents, no matter how nice your renovations.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-group-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Tenant Demographics</h4>
                  <p className="text-gray-700">
                    Your existing tenant base may not be able to afford higher rents. Upgrading forces turnover, leading to vacancy losses that eat into any potential gains.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Competing Properties</h4>
                  <p className="text-gray-700">
                    Other apartment buildings in your area set the rent ceiling. If they're charging $900/month, you can't charge $1,200 just because you renovated.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-percent-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Cap Rate Reality</h4>
                  <p className="text-gray-700">
                    Investors use market cap rates, not your renovation costs. A property in a 8% cap rate market will be valued at 8%, regardless of how much you spent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Comparison Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/60a261e0e0247ca22de74089404af877.jpg"
            alt="Renovation costs vs property value increase analysis"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}